'use client'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, VariantProps } from 'class-variance-authority'
import { ComponentProps, createContext, useContext } from 'react'

import { cn, useFixWidth } from '@/shared/lib'

const groupVariants = cva('group/toggle-group flex', {
  variants: {
    color: {
      gray: '',
      purple: '',
    },
    variant: {
      contained: 'bg-white',
      padded: 'bg-gray-50',
    },
    shape: {
      rounded: 'rounded-lg',
      pill: 'rounded-full',
    },
    size: {
      small: '',
      medium: '',
      large: '',
    },
  },

  compoundVariants: [
    {
      variant: 'padded',
      color: 'gray',
      className: 'bg-gray-50',
    },
    {
      variant: 'padded',
      color: 'purple',
      className: 'bg-purple-50',
    },
    {
      variant: 'padded',
      size: 'small',
      className: 'px-[5px] py-1',
    },
    {
      variant: 'padded',
      size: 'medium',
      className: 'p-[6px]',
    },
    {
      variant: 'padded',
      size: 'large',
      className: 'p-[6px]',
    },
  ],
})

const itemVariants = cva(
  [
    'flex items-center justify-center rounded-sm font-medium transition-all',
    'data-[state=on]:font-semibold',
    '[&_span]:inline-block [&_span]:w-fit',
    '[&_svg]:size-4',
  ],
  {
    variants: {
      color: {
        gray: 'focus-visible:ring-ring text-black [&_svg]:text-gray-500',
        purple: 'text-purple-500 [&_svg]:text-purple-500',
      },
      variant: {
        contained: [
          'border',
          '[&:first-child]:rounded-r-none [&:last-child]:rounded-l-none [&:not(:first-child)]:-ml-[1px] [&:not(:first-child)]:border-l-transparent [&:nth-child(n+2):nth-last-child(n+2)]:rounded-none',
          'ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          'data-[state=on]:!border-[var(--segment-color)] data-[state=on]:focus-visible:border-[var(--segment-color)]',
        ],
        padded: [
          'data-[state=on]:bg-white data-[state=on]:shadow-[0px_0px_3px_0px_rgba(0,0,0,0.11),0px_1px_5px_0px_rgba(0,0,0,0.07)]',
        ],
      },
      shape: {
        rounded: '',
        pill: 'rounded-full',
      },
      size: {
        small: 'typo-label1 h-6 px-[10px] py-0.5',
        medium: 'typo-body2 h-10 px-[10px] py-[7px]',
        large: 'typo-body1 h-12 px-[9px] py-[11px]',
      },
      disabled: {
        true: 'cursor-default',
        false: 'cursor-pointer hover:bg-gray-50/50 active:font-semibold',
      },
    },

    compoundVariants: [
      // variant: Contained - 테두리 색상 설정
      {
        variant: 'contained',
        color: 'gray',
        className:
          'border-black/10 focus-visible:!border-gray-100 focus-visible:ring-gray-200',
      },
      {
        variant: 'contained',
        color: 'purple',
        className:
          'border-purple-500/20 focus-visible:!border-purple-100 focus-visible:ring-purple-200',
      },
      // variant: Padded - 포커스 시 색상 설정
      {
        variant: 'padded',
        color: 'gray',
        className: [
          'focus-visible:ring-3 focus-visible:ring-gray-300',
          'hover:bg-gray-500/10',
        ],
      },
      {
        variant: 'padded',
        color: 'purple',
        className: [
          'focus-visible:ring-3 focus-visible:ring-purple-300',
          'hover:bg-purple-500/10',
        ],
      },
      // variant: Padded - 크기에 따른 기본 텍스트 색상 설정
      {
        variant: 'padded',
        color: 'gray',
        size: 'small',
        className: 'data-[state=off]:text-gray-400',
      },
      // disabled: true - 비활성화 시 색상 설정
      {
        color: 'gray',
        disabled: true,
        className: 'text-gray-200 [&_svg]:text-gray-200',
      },
      {
        color: 'purple',
        disabled: true,
        className: 'border-purple-200 text-purple-200 [&_svg]:text-purple-200',
      },
    ],
  },
)

const getColorClass = (color: GroupProps['color']) => {
  return color === 'gray'
    ? '[--segment-color:var(--color-black)]'
    : '[--segment-color:var(--color-purple-500)]'
}

const ToggleGroupContext = createContext<
  VariantProps<typeof groupVariants> & {
    width?: 'full' | 'fit'
  }
>({
  size: 'large',
  variant: 'contained',
  color: 'gray',
  width: 'full',
})

type GroupProps = ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof groupVariants> & {
    width?: 'full' | 'fit'
  }

export const Root = ({
  color = 'gray',
  variant = 'contained',
  shape = 'rounded',
  size = 'large',
  width = 'full',
  className,
  children,
  ...props
}: GroupProps) => {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      className={cn(
        groupVariants({ size, variant, shape, color }),
        getColorClass(color),
        width === 'full' ? 'w-full' : 'w-fit',
        className,
      )}
      orientation="horizontal"
      {...props}
    >
      <ToggleGroupContext.Provider
        value={{ width, color, shape, variant, size }}
      >
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  )
}

type ItemProps = ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof itemVariants>

export const Item = ({
  disabled = false,
  className,
  children,
  ...props
}: ItemProps) => {
  const {
    width: widthState,
    variant,
    shape,
    color,
    size,
  } = useContext(ToggleGroupContext)
  const { width: widthFromChildren, childrenRef } =
    useFixWidth<HTMLDivElement>(children)

  const width = widthState === 'full' ? null : widthFromChildren

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={variant}
      data-size={size}
      className={cn(
        itemVariants({ variant, shape, color, size, disabled }),
        'min-w-0 shrink-0 focus:z-10 focus-visible:z-10',
        width ? 'w-full' : 'flex-1',
        className,
      )}
      {...(width && { style: { width: `${width}px` } })}
      {...props}
    >
      <div ref={childrenRef}>{children}</div>
    </ToggleGroupPrimitive.Item>
  )
}
